import React, { useState, useEffect } from "react";
import Page from "../components/Page";
import { useSelector, useDispatch } from "react-redux";
import { v4 as uuid } from "uuid";
import { Link } from "react-router-dom";
import GrabElement from "../components/GrabElement";
import LoadingPage from "./LoadingPage";
import LoginPage from "./LoginPage";
import { authenticate } from "../store/actions/auth";
// Add this constant for the new action type
export const UPDATE_USER = "UPDATE_USER";

function ProfilePage() {
	const dispatch = useDispatch();
	const [isEditing, setIsEditing] = useState(false);

	// Accessing Data
	const singleTypeSlug = "user-profile";
	const { data } = useSelector((state) => state.cms);
	const cmsData = data[singleTypeSlug] && data[singleTypeSlug].attributes;
	const collectionSlug = "training-links";
	const collectionData = data[collectionSlug];
	const { user, jwt } = useSelector((state) => state.auth);

	// Add state for edited values
	const [editedUser, setEditedUser] = useState(user || {});

	// Update editedUser when user data changes
	useEffect(() => {
		if (user) {
			setEditedUser(user);
		}
	}, [user]);

	// Handler for input changes
	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setEditedUser((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	// Handler for saving changes
	const handleSave = async () => {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${jwt}`,
					},
					body: JSON.stringify({
						firstName: editedUser.firstName,
						lastName: editedUser.lastName,
						//email: editedUser.email,
						//username: editedUser.email,
						cadetFirstName: editedUser.cadetFirstName,
						cadetLastName: editedUser.cadetLastName,
					}),
				}
			);

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.error?.message || "Failed to update profile");
			}

			const updatedUserData = await response.json();

			// Update Redux store using the correct action structure
			dispatch({
				type: UPDATE_USER,
				user: {
					// Changed from payload to user to match reducer
					...user,
					...updatedUserData,
					firstName: editedUser.firstName,
					lastName: editedUser.lastName,
					//email: editedUser.email,
					cadetFirstName: editedUser.cadetFirstName,
					cadetLastName: editedUser.cadetLastName,
				},
			});

			// Also update auth state to persist changes
			dispatch(
				authenticate(jwt, {
					...user,
					...updatedUserData,
					firstName: editedUser.firstName,
					lastName: editedUser.lastName,
					//email: editedUser.email,
					cadetFirstName: editedUser.cadetFirstName,
					cadetLastName: editedUser.cadetLastName,
				})
			);

			setIsEditing(false);
		} catch (error) {
			console.error("Error updating profile:", error);
			alert("Failed to update profile. Please try again.");
		}
	};

	// Handler for canceling changes
	const handleCancel = () => {
		setEditedUser({
			firstName: user.firstName,
			lastName: user.lastName,
			//email: user.email,
			cadetFirstName: user.cadetFirstName,
			cadetLastName: user.cadetLastName,
		});
		setIsEditing(false);
	};

	if (!user) return <LoginPage />;
	if (!cmsData) return <LoadingPage />;

	const element = GrabElement(collectionData, user.currentRole);

	return (
		<Page
			data-wf-page="63516d8a8642c2a41245f990"
			data-wf-site="634efd6b20b65bfc05be44e4"
		>
			<div className="section wf-section">
				<div className="container containerprofile w-container">
					{/* Add Edit/Save/Cancel buttons */}
					<div className="edit-controls" style={{ marginBottom: "20px" }}>
						{!isEditing ? (
							<button
								onClick={() => {
									setEditedUser(user); // Reset edited values to current user data
									setIsEditing(true);
								}}
								className="edit-button"
							>
								Edit Profile
							</button>
						) : (
							<div className="button-group">
								<button onClick={handleSave} className="save-button">
									Save
								</button>
								<button onClick={handleCancel} className="cancel-button">
									Cancel
								</button>
							</div>
						)}
					</div>

					<div>
						<div className="textuserinfolabel">User ({user.currentRole})</div>
						<div className="textuserinfo">
							{isEditing ? (
								<>
									<input
										type="text"
										name="firstName"
										value={editedUser.firstName}
										onChange={handleInputChange}
										className="edit-input"
									/>{" "}
									<input
										type="text"
										name="lastName"
										value={editedUser.lastName}
										onChange={handleInputChange}
										className="edit-input"
									/>{" "}
								</>
							) : (
								`${user.firstName} ${user.lastName} `
							)}
						</div>
					</div>

					{user.cadetFirstName && user.cadetLastName && (
						<div>
							<div className="textuserinfolabel">Cadets</div>
							<div className="textuserinfo">
								{isEditing ? (
									<>
										<input
											type="text"
											name="cadetFirstName"
											value={editedUser.cadetFirstName}
											onChange={handleInputChange}
											className="edit-input"
										/>{" "}
										<input
											type="text"
											name="cadetLastName"
											value={editedUser.cadetLastName}
											onChange={handleInputChange}
											className="edit-input"
										/>
									</>
								) : (
									`${user.cadetFirstName} ${user.cadetLastName}`
								)}
							</div>
						</div>
					)}

					<div>
						<div className="textuserinfolabel">Email</div>
						<div className="textuserinfo">{user.email}</div>
					</div>
				</div>
			</div>

			{/* Rest of your existing code for resource tiles */}
			<div className="section wf-section">
				<div className="containerpanel w-container">
					{cmsData.resourceTiles &&
						cmsData.resourceTiles.map((item) => (
							<Link
								to={item.slug}
								className="panel w-inline-block"
								key={uuid()}
							>
								<img
									src={item.image.data && item.image.data.attributes.url}
									loading="lazy"
									alt={item.image.alt}
									className="panelimage"
								/>
								<div className="paneltext">
									<div>{item.text}</div>
								</div>
							</Link>
						))}

					{element && element.attributes && (
						<a href={element.attributes.url} className="panel w-inline-block">
							<img
								src={
									cmsData.trainingImage.data &&
									cmsData.trainingImage.data.attributes.url
								}
								loading="lazy"
								alt={cmsData.trainingImageAlt}
								className="panelimage"
							/>
							<div className="paneltext">
								<div>{cmsData.trainingText}</div>
							</div>
						</a>
					)}
				</div>
			</div>
			<div className="section wf-section" />
		</Page>
	);
}

export default ProfilePage;
